import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { StreamChat } from 'stream-chat';

import { GET_CHAT_TOKEN } from 'api/chat/queries';
import ENV from 'api/env';

type UseStreamChatClientReturn = {
  client: StreamChat | null;
  loading: boolean;
  error: string | null;
};

const STREAM_REQUEST_TIMEOUT_MS = 7000;

// One connection per tab: every page that reads chat shares it.
let sharedClient: StreamChat | null = null;
let sharedConnection: Promise<StreamChat> | null = null;

/**
 * The token is signed for a specific GetStream user and `connectUser` has to pass that
 * same id. Reading it off the JWT keeps the backend's choice of user in one place.
 */
function readTokenUserId(token: string): string | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;

    const json = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );

    return typeof json?.user_id === 'string' ? json.user_id : null;
  } catch {
    return null;
  }
}

/**
 * `connectUser` takes a provider rather than the token string so the SDK can re-sign on its
 * own when the token expires in a tab left open. The token already fetched is handed back
 * once, so connecting costs no extra round trip.
 */
async function connect(
  userId: string,
  initialToken: string,
  refreshToken: () => Promise<string>
): Promise<StreamChat> {
  if (!sharedClient)
    sharedClient = StreamChat.getInstance(ENV.GETSTREAM_CHAT_API_KEY, {
      timeout: STREAM_REQUEST_TIMEOUT_MS,
    });
  const client = sharedClient;

  if (client.userID === userId) return client;
  if (sharedConnection) return sharedConnection;

  let unusedToken: string | null = initialToken;
  const tokenProvider = async (): Promise<string> => {
    if (unusedToken) {
      const token = unusedToken;
      unusedToken = null;
      return token;
    }

    return refreshToken();
  };

  sharedConnection = (async () => {
    if (client.userID) await client.disconnectUser();
    await client.connectUser({ id: userId }, tokenProvider);
    return client;
  })();

  try {
    return await sharedConnection;
  } finally {
    sharedConnection = null;
  }
}

/** A GetStream client connected as the Support admin, or null while it is not ready. */
export const useStreamChatClient = (): UseStreamChatClientReturn => {
  const [client, setClient] = useState<StreamChat | null>(null);
  const [connecting, setConnecting] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    data,
    loading: tokenLoading,
    error: tokenError,
    refetch,
  } = useQuery<{
    getChatToken: string;
  }>(GET_CHAT_TOKEN, { fetchPolicy: 'cache-first' });

  const token = data?.getChatToken ?? null;

  // The SDK holds the provider for the life of the connection, so it must not close over
  // one render's refetch.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const refreshToken = useCallback(async () => {
    const result = await refetchRef.current();
    const next = result.data?.getChatToken;
    if (!next) throw new Error('Could not refresh the chat token.');

    return next;
  }, []);

  useEffect(() => {
    if (!ENV.GETSTREAM_CHAT_API_KEY) {
      setConnecting(false);
      setError('Chat is not configured for this environment.');
      return;
    }

    if (tokenError) {
      setConnecting(false);
      setError('Could not get a chat token.');
      return;
    }

    if (!token) return;

    const userId = readTokenUserId(token);
    if (!userId) {
      setConnecting(false);
      setError('Chat token is not readable.');
      return;
    }

    let cancelled = false;
    setConnecting(true);

    connect(userId, token, refreshToken)
      .then((connected) => {
        if (cancelled) return;
        setClient(connected);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setClient(null);
        setError('Could not connect to chat.');
      })
      .finally(() => {
        if (!cancelled) setConnecting(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, tokenError, refreshToken]);

  return { client, loading: connecting || tokenLoading, error };
};
